import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { ArrowLeft, Edit, Printer, Trash2, DollarSign, Calendar, User, Settings, Save, X } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';

interface CobrancaDetalhesData {
  id: string;
  cliente: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  dataVencimento: string;
  status: string;
  tipo: 'avulsa' | 'assinatura';
  dataCriacao: string;
  dataAtualizacao: string;
  dataPagamento?: string;
  valorPago?: number;
}

const CobrancaDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [cobranca, setCobranca] = useState<CobrancaDetalhesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    valor: 0,
    dataVencimento: '',
    descricao: '',
    formaPagamento: '',
    status: '',
    valorPago: 0,
    dataPagamento: ''
  });

  useEffect(() => {
    if (id) {
      loadCobrancaDetalhes();
    }
  }, [id]);

  const loadCobrancaDetalhes = React.useCallback(async () => {
    if (!id || !userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      
      // Buscar dados da cobrança (pode ser pagamento ou assinatura)
      const [pagamentos, assinaturas, alunos] = await Promise.all([
        firebaseService.getPagamentos(userProfile.empresaCnpj),
        firebaseService.getAssinaturas(userProfile.empresaCnpj),
        firebaseService.getAllAlunos(userProfile.empresaCnpj)
      ]);

      // Procurar em pagamentos primeiro
      let pagamento = pagamentos.find(p => p.id === id);
      let assinatura: any = null;
      let tipo: 'avulsa' | 'assinatura' = 'avulsa';
      let cobrancaEncontrada = false;

      if (pagamento) {
        const aluno = alunos.find(a => a.id === pagamento!.alunoId);
        setCobranca({
          id: pagamento.id, // ID interno do Firebase do documento
          cliente: aluno?.nome || 'Cliente não encontrado',
          valor: pagamento.valor,
          descricao: `Pagamento - ${pagamento.externalReference || 'Sem referência'}`,
          formaPagamento: pagamento.tipoPagamento,
          dataVencimento: pagamento.dataVencimento,
          status: pagamento.status,
          tipo: 'avulsa',
          dataCriacao: pagamento.createDate,
          dataAtualizacao: pagamento.updateDate,
          dataPagamento: pagamento.dataPagamento,
          valorPago: pagamento.valorPago
        });
        cobrancaEncontrada = true;
      } else {
        // Procurar em mensalidades (cobranças de assinaturas)
        const mensalidades = await firebaseService.getAllMensalidadesComLocalizacao(userProfile.empresaCnpj);
        const mensalidade = mensalidades.find(m => m.id === id);
        
        if (mensalidade) {
          const aluno = alunos.find(a => a.id === mensalidade.alunoId);
          const assinaturaEncontrada = assinaturas.find(a => a.id === mensalidade.assinaturaId);
          setCobranca({
            id: mensalidade.id,
            cliente: aluno?.nome || 'Cliente não encontrado',
            valor: mensalidade.valor,
            descricao: `Mensalidade ${mensalidade.numero} - ${assinaturaEncontrada?.description || 'Assinatura'}`,
            formaPagamento: assinaturaEncontrada?.billingType || 'PIX',
            dataVencimento: mensalidade.dataVencimento,
            status: mensalidade.status,
            tipo: 'assinatura',
            dataCriacao: mensalidade.createDate,
            dataAtualizacao: mensalidade.updateDate,
            dataPagamento: mensalidade.dataPagamento,
            valorPago: mensalidade.valorPago
          });
          cobrancaEncontrada = true;
        } else {
          // Procurar em assinaturas (fallback)
          assinatura = assinaturas.find(a => a.id === id);
          if (assinatura) {
            const aluno = alunos.find(a => a.id === assinatura!.alunoId);
            setCobranca({
              id: assinatura.id, // ID interno do Firebase do documento
              cliente: aluno?.nome || 'Cliente não encontrado',
              valor: assinatura.value,
              descricao: assinatura.description,
              formaPagamento: assinatura.billingType,
              dataVencimento: assinatura.nextDueDate,
              status: assinatura.status,
              tipo: 'assinatura',
              dataCriacao: assinatura.createDate,
              dataAtualizacao: assinatura.updateDate
            });
            cobrancaEncontrada = true;
          }
        }
      }

      if (!cobrancaEncontrada) {
        navigate('/cobrancas');
        return;
      }
      
    } catch (error) {
      console.error('Erro ao carregar detalhes da cobrança:', error);
    } finally {
      setLoading(false);
    }
  }, [id, userProfile?.empresaCnpj, navigate]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      // Se a data já está no formato DD/MM/YYYY, retornar como está
      if (dateString.includes('/')) {
        return dateString;
      }
      
      // Se está no formato ISO ou outro formato, converter
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn('Data inválida:', dateString);
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar data:', error, dateString);
      return 'Data inválida';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'vencido':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'pendente':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pago':
        return 'Pago';
      case 'vencido':
        return 'Vencido';
      case 'pendente':
        return 'Pendente';
      default:
        return status;
    }
  };

  const handleReceberPagamento = async () => {
    if (!cobranca || !userProfile?.empresaCnpj) return;
    
    try {
      if (cobranca.tipo === 'avulsa') {
        const pagamentos = await firebaseService.getPagamentos(userProfile.empresaCnpj);
        const pagamento = pagamentos.find(p => p.id === cobranca.id);
        if (pagamento) {
          await firebaseService.updatePagamentoStatus(userProfile.empresaCnpj, pagamento, 'pago', cobranca.valor);
          showSuccess('Sucesso', 'Pagamento marcado como recebido!');
        }
      } else {
        await firebaseService.updateAssinaturaStatus(cobranca.id, 'ACTIVE');
        showSuccess('Sucesso', 'Assinatura marcada como ativa!');
      }
      loadCobrancaDetalhes();
    } catch (error) {
      console.error('Erro ao receber pagamento:', error);
      showError('Erro', 'Erro ao processar pagamento. Tente novamente.');
    }
  };

  const handleRenovarCobranca = async () => {
    if (!cobranca || !userProfile?.empresaCnpj) return;
    
    try {
      if (cobranca.tipo === 'avulsa') {
        const novaDataVencimento = new Date();
        novaDataVencimento.setDate(novaDataVencimento.getDate() + 30);
        
        const novoPagamento = {
          alunoId: cobranca.id,
          valor: cobranca.valor,
          dataVencimento: novaDataVencimento.toISOString().split('T')[0],
          descricao: `Renovação - ${cobranca.descricao}`,
          tipoPagamento: cobranca.formaPagamento,
          status: 'pendente'
        };
        
        await firebaseService.createPagamento(userProfile.empresaCnpj, novoPagamento);
        showSuccess('Sucesso', 'Cobrança renovada por mais 30 dias!');
      } else {
        await firebaseService.updateAssinaturaStatus(cobranca.id, 'ACTIVE');
        showSuccess('Sucesso', 'Assinatura renovada com sucesso!');
      }
      loadCobrancaDetalhes();
    } catch (error) {
      console.error('Erro ao renovar cobrança:', error);
      showError('Erro', 'Erro ao renovar cobrança. Tente novamente.');
    }
  };

  // Função auxiliar para criar elemento de forma segura
  const createSafeElement = (doc: Document, tag: string, className?: string, textContent?: string): HTMLElement => {
    const element = doc.createElement(tag);
    if (className) element.className = className;
    if (textContent) element.textContent = textContent; // textContent é seguro, previne XSS
    return element;
  };

  // Função auxiliar para criar campo de formulário de forma segura
  const createField = (doc: Document, label: string, value: string): HTMLElement => {
    const field = createSafeElement(doc, 'div', 'field');
    const labelSpan = createSafeElement(doc, 'span', 'label', label);
    const valueSpan = createSafeElement(doc, 'span', 'value', value);
    field.appendChild(labelSpan);
    field.appendChild(valueSpan);
    return field;
  };

  const handleGerarNotaFiscal = () => {
    if (!cobranca) return;
    
    // Gerar nota fiscal em nova janela usando manipulação segura do DOM
    const nfWindow = window.open('', '_blank');
    if (!nfWindow) return;

    const doc = nfWindow.document;
    
    // Criar elementos HTML de forma segura
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const body = doc.createElement('body');
    
    // Título
    const title = doc.createElement('title');
    title.textContent = `Nota Fiscal - ${cobranca.cliente}`;
    head.appendChild(title);
    
    // Estilos
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; margin: 20px; }
      .nf { border: 1px solid #000; padding: 20px; max-width: 600px; }
      .header { text-align: center; font-weight: bold; margin-bottom: 20px; border-bottom: 1px solid #000; padding-bottom: 10px; }
      .field { margin: 10px 0; display: flex; }
      .label { font-weight: bold; width: 150px; }
      .value { margin-left: 10px; }
      .total { border-top: 2px solid #000; margin-top: 20px; padding-top: 10px; font-weight: bold; font-size: 18px; }
    `;
    head.appendChild(style);
    
    // Conteúdo
    const nfDiv = createSafeElement(doc, 'div', 'nf');
    const header = createSafeElement(doc, 'div', 'header', 'NOTA FISCAL');
    nfDiv.appendChild(header);
    
    // Campos usando textContent para prevenir XSS
    nfDiv.appendChild(createField(doc, 'Cliente:', cobranca.cliente || ''));
    nfDiv.appendChild(createField(doc, 'Serviço:', cobranca.descricao || ''));
    nfDiv.appendChild(createField(doc, 'Data de Vencimento:', formatDate(cobranca.dataVencimento)));
    nfDiv.appendChild(createField(doc, 'Forma de Pagamento:', cobranca.formaPagamento || ''));
    nfDiv.appendChild(createField(doc, 'Status:', getStatusText(cobranca.status)));
    
    // Total
    const totalDiv = createSafeElement(doc, 'div', 'total');
    const totalLabel = createSafeElement(doc, 'span', 'label', 'Valor Total:');
    const totalValue = createSafeElement(doc, 'span', 'value', formatCurrency(cobranca.valor));
    totalDiv.appendChild(totalLabel);
    totalDiv.appendChild(totalValue);
    nfDiv.appendChild(totalDiv);
    
    // Rodapé
    const footer = createSafeElement(doc, 'div');
    footer.style.marginTop = '30px';
    footer.style.textAlign = 'center';
    footer.style.fontSize = '12px';
    footer.style.color = '#666';
    footer.textContent = 'Esta é uma nota fiscal gerada automaticamente pelo sistema SwimmingPool';
    nfDiv.appendChild(footer);
    
    body.appendChild(nfDiv);
    html.appendChild(head);
    html.appendChild(body);
    doc.appendChild(html);
  };

  const handleImprimirBoleto = () => {
    if (!cobranca) return;
    
    // Abrir janela de impressão com dados do boleto usando manipulação segura do DOM
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doc = printWindow.document;
    
    // Criar elementos HTML de forma segura
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const body = doc.createElement('body');
    
    // Título
    const title = doc.createElement('title');
    title.textContent = `Boleto - ${cobranca.cliente}`;
    head.appendChild(title);
    
    // Estilos
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; margin: 20px; }
      .boleto { border: 2px solid #000; padding: 20px; max-width: 400px; }
      .header { text-align: center; font-weight: bold; margin-bottom: 20px; }
      .field { margin: 10px 0; }
      .label { font-weight: bold; }
      .value { margin-left: 10px; }
    `;
    head.appendChild(style);
    
    // Conteúdo
    const boletoDiv = createSafeElement(doc, 'div', 'boleto');
    const header = createSafeElement(doc, 'div', 'header', 'BOLETO BANCÁRIO');
    boletoDiv.appendChild(header);
    
    // Campos usando textContent para prevenir XSS
    boletoDiv.appendChild(createField(doc, 'Cliente:', cobranca.cliente || ''));
    boletoDiv.appendChild(createField(doc, 'Valor:', formatCurrency(cobranca.valor)));
    boletoDiv.appendChild(createField(doc, 'Descrição:', cobranca.descricao || ''));
    boletoDiv.appendChild(createField(doc, 'Vencimento:', formatDate(cobranca.dataVencimento)));
    boletoDiv.appendChild(createField(doc, 'Forma de Pagamento:', cobranca.formaPagamento || ''));
    boletoDiv.appendChild(createField(doc, 'Status:', getStatusText(cobranca.status)));
    
    body.appendChild(boletoDiv);
    html.appendChild(head);
    html.appendChild(body);
    doc.appendChild(html);
    
    // Aguardar carregamento antes de imprimir
    setTimeout(() => {
      printWindow.print();
    }, 100);
  };

  const handleEnviarEmail = () => {
    if (!cobranca) return;
    
    const assunto = `Boleto - ${cobranca.cliente}`;
    const corpo = `
Olá ${cobranca.cliente},

Segue o boleto para pagamento:

Valor: ${formatCurrency(cobranca.valor)}
Descrição: ${cobranca.descricao}
Vencimento: ${formatDate(cobranca.dataVencimento)}
Forma de Pagamento: ${cobranca.formaPagamento}

Para pagar, acesse o link: [Link do boleto]

Atenciosamente,
Equipe SwimmingPool
    `;
    
    const mailtoLink = `mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
    window.open(mailtoLink);
  };

  const handleExcluirCobranca = async () => {
    if (!cobranca || !userProfile?.empresaCnpj) return;
    
    showConfirm(
      'Excluir Cobrança',
      `Tem certeza que deseja excluir a cobrança de ${cobranca.cliente}?`,
      async () => {
        try {
          if (cobranca.tipo === 'avulsa') {
            await firebaseService.deletePagamento(userProfile.empresaCnpj, cobranca.id);
          } else {
            await firebaseService.deleteAssinatura(userProfile.empresaCnpj, cobranca.id);
          }
          showSuccess('Sucesso', 'Cobrança excluída com sucesso!');
          navigate('/cobrancas');
        } catch (error) {
          console.error('Erro ao excluir cobrança:', error);
          showError('Erro', 'Erro ao excluir cobrança. Tente novamente.');
        }
      },
      {
        confirmText: 'Excluir',
        cancelText: 'Cancelar'
      }
    );
  };

  const handleEditCobranca = () => {
    if (!cobranca) return;
    
    console.log('🔍 Dados da cobrança para edição:', cobranca);
    
    // Converter data para formato do input (YYYY-MM-DD)
    let dataVencimento = '';
    if (cobranca.dataVencimento) {
      try {
        // Se já está no formato DD/MM/YYYY, converter para YYYY-MM-DD
        if (cobranca.dataVencimento.includes('/')) {
          const [dia, mes, ano] = cobranca.dataVencimento.split('/');
          dataVencimento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
        } else {
          // Se está em formato ISO ou outro, tentar converter
          const date = new Date(cobranca.dataVencimento);
          if (!isNaN(date.getTime())) {
            dataVencimento = date.toISOString().split('T')[0];
          }
        }
      } catch (error) {
        console.error('Erro ao converter data:', error, cobranca.dataVencimento);
        dataVencimento = '';
      }
    }
    
    // Converter data de pagamento para formato do input (YYYY-MM-DD)
    let dataPagamento = '';
    if (cobranca.dataPagamento) {
      try {
        // Se está em formato ISO, extrair apenas a data
        if (cobranca.dataPagamento.includes('T')) {
          dataPagamento = cobranca.dataPagamento.split('T')[0];
        } else if (cobranca.dataPagamento.includes('/')) {
          // Se está no formato DD/MM/YYYY, converter para YYYY-MM-DD
          const [dia, mes, ano] = cobranca.dataPagamento.split('/');
          dataPagamento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
        } else {
          // Assumir que já está no formato YYYY-MM-DD
          dataPagamento = cobranca.dataPagamento;
        }
      } catch (error) {
        console.error('Erro ao converter data de pagamento:', error);
        dataPagamento = '';
      }
    }
    
    const editDataToSet = {
      valor: cobranca.valor,
      dataVencimento: dataVencimento,
      descricao: cobranca.descricao,
      formaPagamento: cobranca.formaPagamento,
      status: cobranca.status,
      valorPago: cobranca.valorPago || 0,
      dataPagamento: dataPagamento
    };
    
    console.log('📝 Dados para edição:', editDataToSet);
    
    setEditData(editDataToSet);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!cobranca || !userProfile?.empresaCnpj) return;
    
    try {
      // Determinar status: se valorPago e dataPagamento forem preenchidos, status = 'pago'
      let statusFinal = editData.status;
      if (editData.valorPago > 0 && editData.dataPagamento) {
        statusFinal = 'pago';
      }
      
      if (cobranca.tipo === 'avulsa') {
        // Atualizar pagamento avulso
        const pagamentos = await firebaseService.getPagamentos(userProfile.empresaCnpj);
        const pagamento = pagamentos.find(p => p.id === cobranca.id);
        if (pagamento) {
          const updatedPagamento = {
            ...pagamento,
            valor: editData.valor,
            dataVencimento: editData.dataVencimento,
            descricao: editData.descricao,
            tipoPagamento: editData.formaPagamento,
            status: statusFinal,
            updateDate: new Date().toISOString()
          };
          
          // Adicionar valorPago e dataPagamento se preenchidos
          if (editData.valorPago > 0) {
            updatedPagamento.valorPago = editData.valorPago;
          }
          if (editData.dataPagamento) {
            // Converter para formato ISO se necessário
            if (editData.dataPagamento.includes('T')) {
              updatedPagamento.dataPagamento = editData.dataPagamento;
            } else {
              updatedPagamento.dataPagamento = new Date(editData.dataPagamento + 'T00:00:00').toISOString();
            }
          }
          
          await firebaseService.updatePagamento(userProfile.empresaCnpj, updatedPagamento);
        }
      } else {
        // Atualizar mensalidade
        const mensalidades = await firebaseService.getAllMensalidadesComLocalizacao(userProfile.empresaCnpj);
        const mensalidade = mensalidades.find(m => m.id === cobranca.id);
        
        if (mensalidade && mensalidade.anoMes && mensalidade.alunoId) {
          const dadosAtualizacao: any = {
            valor: editData.valor,
            dataVencimento: editData.dataVencimento,
            status: statusFinal
          };
          
          if (editData.valorPago > 0) {
            dadosAtualizacao.valorPago = editData.valorPago;
          }
          if (editData.dataPagamento) {
            // Converter para formato ISO se necessário
            if (editData.dataPagamento.includes('T')) {
              dadosAtualizacao.dataPagamento = editData.dataPagamento;
            } else {
              dadosAtualizacao.dataPagamento = new Date(editData.dataPagamento + 'T00:00:00').toISOString();
            }
          }
          
          await firebaseService.updateMensalidade(
            userProfile.empresaCnpj,
            mensalidade.id,
            mensalidade.anoMes,
            mensalidade.alunoId,
            dadosAtualizacao
          );
        } else {
          // Fallback: atualizar assinatura
          const assinaturas = await firebaseService.getAssinaturas(userProfile.empresaCnpj);
          const assinatura = assinaturas.find(a => a.id === cobranca.id);
          if (assinatura) {
            const updatedAssinatura = {
              ...assinatura,
              value: editData.valor,
              nextDueDate: editData.dataVencimento,
              description: editData.descricao,
              billingType: editData.formaPagamento,
              status: statusFinal,
              updateDate: new Date().toISOString()
            };
            await firebaseService.updateAssinatura(userProfile.empresaCnpj, updatedAssinatura);
          }
        }
      }
      
      // Atualizar dados locais
      setCobranca({
        ...cobranca,
        valor: editData.valor,
        dataVencimento: editData.dataVencimento,
        descricao: editData.descricao,
        formaPagamento: editData.formaPagamento,
        status: statusFinal,
        valorPago: editData.valorPago > 0 ? editData.valorPago : cobranca.valorPago,
        dataPagamento: editData.dataPagamento || cobranca.dataPagamento
      });
      
      setIsEditing(false);
      showSuccess('Sucesso', 'Cobrança atualizada com sucesso!');
      loadCobrancaDetalhes();
    } catch (error) {
      console.error('Erro ao atualizar cobrança:', error);
      showError('Erro', 'Erro ao atualizar cobrança. Tente novamente.');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({
      valor: 0,
      dataVencimento: '',
      descricao: '',
      formaPagamento: '',
      status: '',
      valorPago: 0,
      dataPagamento: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!cobranca) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">Cobrança não encontrada</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cobrancas')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Voltar</span>
          </button>
          
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Detalhes</span>
              </li>
            </ol>
          </nav>
        </div>

        <div className="flex items-center space-x-3">
          <button 
            onClick={handleEditCobranca}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Edit className="h-4 w-4" />
            <span>Editar cobrança</span>
          </button>
          <button 
            onClick={handleReceberPagamento}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <DollarSign className="h-4 w-4" />
            <span>Receber pagamento</span>
          </button>
          <button 
            onClick={handleImprimirBoleto}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Printer className="h-4 w-4" />
            <span>Imprimir boleto</span>
          </button>
          <button 
            onClick={handleExcluirCobranca}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            <span>Excluir cobrança</span>
          </button>
        </div>
      </div>

      {/* Dados da Cobrança */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Dados da cobrança</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ID da Cobrança</label>
            <div className="text-sm font-mono text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
              {cobranca.id}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <span className="text-green-600 text-sm font-medium">
                  {cobranca.cliente.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900">{cobranca.cliente}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(cobranca.valor)}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Forma de pagamento</label>
            <div className="text-sm text-gray-900">{cobranca.formaPagamento}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data de vencimento</label>
            <div className="text-sm text-gray-900">{formatDate(cobranca.dataVencimento)}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <div className="text-sm text-gray-900">
              {cobranca.tipo === 'avulsa' ? 'Pagamento Avulso' : 'Assinatura'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(cobranca.status)}`}>
              {getStatusText(cobranca.status)}
            </span>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
          <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
            {cobranca.descricao}
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Ações rápidas</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={handleReceberPagamento}
            className="flex flex-col items-center space-y-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <DollarSign className="h-6 w-6 text-green-600" />
            <span className="text-sm font-medium">Receber</span>
          </button>
          
          <button 
            onClick={handleRenovarCobranca}
            className="flex flex-col items-center space-y-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Calendar className="h-6 w-6 text-blue-600" />
            <span className="text-sm font-medium">Renovar</span>
          </button>
          
          <button 
            onClick={handleGerarNotaFiscal}
            className="flex flex-col items-center space-y-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Settings className="h-6 w-6 text-purple-600" />
            <span className="text-sm font-medium">Nota Fiscal</span>
          </button>
          
          <button 
            onClick={handleEnviarEmail}
            className="flex flex-col items-center space-y-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <User className="h-6 w-6 text-orange-600" />
            <span className="text-sm font-medium">Enviar Email</span>
          </button>
        </div>
      </div>

      {/* Modal de Edição */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar cobrança</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.valor}
                  onChange={(e) => setEditData({ ...editData, valor: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de vencimento</label>
                <input
                  type="date"
                  value={editData.dataVencimento}
                  onChange={(e) => setEditData({ ...editData, dataVencimento: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <textarea
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Forma de pagamento</label>
                <select
                  value={editData.formaPagamento}
                  onChange={(e) => setEditData({ ...editData, formaPagamento: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="PIX">PIX</option>
                  <option value="BOLETO">Boleto</option>
                  <option value="CARTAO_CREDITO">Cartão de Crédito</option>
                  <option value="CARTAO_DEBITO">Cartão de Débito</option>
                  <option value="DINHEIRO">Dinheiro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor Pago</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editData.valorPago}
                  onChange={(e) => setEditData({ ...editData, valorPago: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de Pagamento</label>
                <input
                  type="date"
                  value={editData.dataPagamento}
                  onChange={(e) => setEditData({ ...editData, dataPagamento: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {editData.valorPago > 0 && editData.dataPagamento && (
                  <p className="text-xs text-gray-500 mt-1">O status será atualizado automaticamente para "Pago"</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                  <option value="vencido">Vencido</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                <Save className="h-4 w-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationComponent />
    </div>
  );
};

export default CobrancaDetalhes;
